/**
 * Снятие реального outbound видео-FPS публикуемого холста рум-агента.
 *
 * `drawing_fps` меряет темп цикла отрисовки (попытки), а не доставленные кадры.
 * Здесь из getStats читаем честные числа: media-source.framesPerSecond — реальный
 * выход холста в поток, outbound-rtp.framesPerSecond + qualityLimitationReason —
 * что реально кодирует и отдаёт энкодер.
 *
 * Перебираем живые PC из peer-connection-registry; публикующий PC — тот, чей отчёт
 * содержит outbound-rtp video. Перебор дёшев: PC обычно 1-2 на сессию.
 */
import { finiteOrUndefined } from '../../../../../../shared/helpers/finite';
import { getLivePeerConnections } from './peer-connection-registry';

export type OutboundVideoSample = {
    mediaSourceFps?: number;
    outboundFps?: number;
    // qualityLimitationReason числом для графика (yasm.abs строку не примет); none → все 0.
    outboundLimitedCpu?: number;
    outboundLimitedBandwidth?: number;
    outboundLimitedOther?: number;
    // строка — только для DTMF-оверлея.
    outboundQualityLimitation?: RTCQualityLimitationReason;
};

// media-source не типизирован в DOM-либе — читаем нужные поля через локальный тип.
type MediaSourceVideo = {
    kind?: string;
    framesPerSecond?: number;
};

async function getStatsForPc(pc: RTCPeerConnection): Promise<RTCStatsReport | undefined> {
    try {
        return await pc.getStats();
    } catch {
        return undefined;
    }
}

function isOutboundRtpVideo(entry: RTCStats): entry is RTCOutboundRtpStreamStats {
    return entry.type === 'outbound-rtp' && 'kind' in entry && entry.kind === 'video';
}

function isMediaSourceVideo(entry: RTCStats): entry is RTCStats & MediaSourceVideo {
    return entry.type === 'media-source' && 'kind' in entry && entry.kind === 'video';
}

function findMediaSourceVideo(report: RTCStatsReport, mediaSourceId?: string): MediaSourceVideo | undefined {
    let fallback: MediaSourceVideo | undefined;
    for (const entry of report.values()) {
        if (!isMediaSourceVideo(entry)) {
            continue;
        }
        // Привязываем media-source к нашему outbound по id; иначе берём первый видео-источник.
        if (mediaSourceId === undefined || entry.id === mediaSourceId) {
            return entry;
        }
        fallback = fallback ?? entry;
    }
    return fallback;
}

function extractOutboundVideoSample(report: RTCStatsReport | undefined): OutboundVideoSample | undefined {
    if (!report) {
        return undefined;
    }

    let outbound: RTCOutboundRtpStreamStats | undefined;
    for (const entry of report.values()) {
        if (isOutboundRtpVideo(entry)) {
            outbound = entry;
            break;
        }
    }
    if (!outbound) {
        return undefined;
    }

    const mediaSource = findMediaSourceVideo(report, outbound.mediaSourceId);
    const reason = outbound.qualityLimitationReason;

    return {
        mediaSourceFps: finiteOrUndefined(mediaSource?.framesPerSecond),
        outboundFps: finiteOrUndefined(outbound.framesPerSecond),
        outboundLimitedCpu: reason === undefined ? undefined : Number(reason === 'cpu'),
        outboundLimitedBandwidth: reason === undefined ? undefined : Number(reason === 'bandwidth'),
        outboundLimitedOther: reason === undefined ? undefined : Number(reason === 'other'),
        outboundQualityLimitation: reason,
    };
}

export async function collectOutboundVideoSample(): Promise<OutboundVideoSample | undefined> {
    const pcs = getLivePeerConnections();
    for (const pc of pcs) {
        const report = await getStatsForPc(pc);
        const sample = extractOutboundVideoSample(report);
        if (sample) {
            return sample;
        }
    }
    return undefined;
}
